/* Clothxtra product section */
if (!customElements.get('cx-product')) {
  class CxProduct extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-cx-variants]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-cx-strings]').textContent);
      this.fieldsets = Array.from(this.querySelectorAll('[data-cx-option]'));
      this.idInput = this.querySelector('form input[name="id"]');
      this.submit = this.querySelector('[data-cx-submit]');
      this.priceEl = this.querySelector('[data-cx-price]');
      this.skuEl = this.querySelector('[data-cx-sku]');
      this.stockEl = this.querySelector('[data-cx-stock]');
      this.gallery = this.querySelector('[data-cx-gallery]');
      this.countEl = this.querySelector('[data-cx-count]');
      this.barEl = this.querySelector('[data-cx-bar]');

      this.addEventListener('change', (event) => {
        const fs = event.target.closest('[data-cx-option]');
        if (fs && event.target.type === 'radio') this.onOptionChange(Number(fs.dataset.cxOption));
      });

      // quantity − / +
      this.querySelectorAll('[data-cx-qty]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const input = this.querySelector('[data-cx-qty-input]');
          if (!input) return;
          input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.cxQty));
        })
      );
      this.querySelector('[data-cx-qty-input]')?.addEventListener('blur', (e) => {
        e.target.value = Math.max(1, parseInt(e.target.value, 10) || 1);
      });

      // size guide popup (delegated, so it works wherever the block sits)
      this.addEventListener('click', (e) => {
        const dialog = this.querySelector('[data-cx-guide]');
        if (!dialog) return;
        if (e.target.closest('[data-cx-guide-open]')) {
          e.preventDefault();
          if (typeof dialog.showModal === 'function') dialog.showModal();
          else dialog.setAttribute('open', '');
          document.documentElement.style.overflow = 'hidden';
          return;
        }
        if (e.target.closest('[data-cx-guide-close]') || e.target === dialog) {
          if (typeof dialog.close === 'function') dialog.close();
          else dialog.removeAttribute('open');
        }
      });
      this.querySelector('[data-cx-guide]')?.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
      });

      // policy popups (price note / delivery estimate blocks)
      this.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cx-pop-open]');
        if (opener) {
          const d = document.getElementById(opener.dataset.cxPopOpen);
          if (!d) return;
          e.preventDefault();
          if (typeof d.showModal === 'function') d.showModal();
          else d.setAttribute('open', '');
          document.documentElement.style.overflow = 'hidden';
          return;
        }
        const d = e.target.closest('dialog[data-cx-pop]');
        if (d && (e.target.closest('[data-cx-pop-close]') || e.target === d)) {
          if (typeof d.close === 'function') d.close();
          else d.removeAttribute('open');
        }
      });
      this.querySelectorAll('dialog[data-cx-pop]').forEach((d) =>
        d.addEventListener('close', () => { document.documentElement.style.overflow = ''; })
      );

      // mobile gallery counter
      if (this.gallery && (this.countEl || this.barEl)) {
        let raf;
        this.gallery.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
              const idx = this.slideIndex();
              if (this.countEl) this.countEl.textContent = idx + 1;
              this.setBar(idx);
              this.setActiveThumb(idx);
            });
          },
          { passive: true }
        );
      }

      this.updateAvailability();
      this.setupThumbs();
      this.setupSticky();

      // "Back to collection": return to the exact page they came from (keeps filters + scroll)
      this.querySelector('[data-cx-back]')?.addEventListener('click', (e) => {
        try {
          const ref = document.referrer ? new URL(document.referrer) : null;
          const target = new URL(e.currentTarget.href, window.location.origin);
          if (ref && ref.origin === window.location.origin && ref.pathname === target.pathname && window.history.length > 1) {
            e.preventDefault();
            window.history.back();
          }
        } catch (err) {
          /* fall back to the normal link */
        }
      });
    }

    /* Smart sticky columns: a column that fits on screen sticks below the header;
       a taller one sticks by its bottom edge, so its end (e.g. Add to bag) is always reachable. */
    setupSticky() {
      const cols = [this.querySelector('.cx-pdp__media'), this.querySelector('.cx-pdp__info')].filter(Boolean);
      if (!cols.length) return;
      const gap = 20;
      const update = () => {
        const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const top = headerH + gap;
        cols.forEach((col) => {
          const fits = col.offsetHeight + top + gap <= window.innerHeight;
          col.style.setProperty('--cx-top', `${fits ? top : window.innerHeight - col.offsetHeight - gap}px`);
        });
      };
      update();
      if ('ResizeObserver' in window) {
        this.stickyRO = new ResizeObserver(update);
        cols.forEach((c) => this.stickyRO.observe(c));
      }
      window.addEventListener('resize', update);
      // images load late and change the column height
      this.querySelectorAll('.cx-gal img').forEach((img) => !img.complete && img.addEventListener('load', update, { once: true }));
    }

    /* ---- Thumbnails + large image ---- */
    setupThumbs() {
      this.media = this.querySelector('.cx-pdp__media');
      this.thumbs = this.querySelector('[data-cx-thumbs]');
      if (!this.thumbs || !this.gallery) return;
      this.items = Array.from(this.gallery.querySelectorAll('.cx-gal__item'));

      this.thumbs.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cx-thumb]');
        if (btn) this.goTo(Number(btn.dataset.cxThumb));
      });

      // desktop without the mobile counter: still track the active slide
      if (!this.countEl && !this.barEl) {
        this.gallery.addEventListener(
          'scroll',
          () => this.setActiveThumb(this.slideIndex()),
          { passive: true }
        );
      }

      // thumbnail column = height of the big image (desktop), scrolls if there are many
      const frame = this.querySelector('.cx-gal__frame');
      const fit = () => {
        const desktop = window.matchMedia('(min-width: 990px)').matches;
        this.thumbs.style.maxHeight = desktop ? `${frame.offsetHeight}px` : '';
        this.thumbs.style.overflowY = desktop ? 'auto' : '';
      };
      fit();
      if ('ResizeObserver' in window) new ResizeObserver(fit).observe(frame);

      // start on the selected variant's image
      const lead = this.thumbs.querySelector('.is-active');
      if (lead) this.goTo(Number(lead.dataset.cxThumb), true);
    }

    slideWidth() {
      const item = this.gallery && this.gallery.querySelector('.cx-gal__item');
      return (item && item.offsetWidth) || (this.gallery && this.gallery.clientWidth) || 1;
    }

    slideIndex() {
      const g = this.gallery;
      const count = g.querySelectorAll('.cx-gal__item').length;
      if (g.scrollLeft >= g.scrollWidth - g.clientWidth - 2) return Math.max(count - 1, 0);
      return Math.min(Math.round(g.scrollLeft / this.slideWidth()), count - 1);
    }

    setBar(index) {
      if (!this.barEl) return;
      this.barEl.querySelectorAll('span').forEach((s, i) => s.classList.toggle('is-active', i === index));
    }

    goTo(index, instant) {
      if (!this.gallery) return;
      this.gallery.scrollTo({ left: index * this.slideWidth(), behavior: instant ? 'auto' : 'smooth' });
      this.setActiveThumb(index);
    }

    setActiveThumb(index) {
      if (!this.thumbs) return;
      this.thumbs.querySelectorAll('[data-cx-thumb]').forEach((b, i) => {
        const on = i === index;
        b.classList.toggle('is-active', on);
        if (on) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
        if (on) {
          // keep the active thumbnail visible inside its own strip (not the page)
          const li = b.parentElement;
          const desktop = this.thumbs.scrollHeight > this.thumbs.clientHeight + 2;
          const t = this.thumbs;
          if (desktop) {
            // only move the strip when the active thumbnail is out of view
            if (li.offsetTop < t.scrollTop) t.scrollTo({ top: li.offsetTop, behavior: 'smooth' });
            else if (li.offsetTop + li.offsetHeight > t.scrollTop + t.clientHeight) {
              t.scrollTo({ top: li.offsetTop + li.offsetHeight - t.clientHeight, behavior: 'smooth' });
            }
          } else if (t.scrollWidth > t.clientWidth + 2) {
            if (li.offsetLeft < t.scrollLeft) t.scrollTo({ left: li.offsetLeft - 15, behavior: 'smooth' });
            else if (li.offsetLeft + li.offsetWidth > t.scrollLeft + t.clientWidth) {
              t.scrollTo({ left: li.offsetLeft + li.offsetWidth - t.clientWidth + 15, behavior: 'smooth' });
            }
          }
        }
      });
    }

    disconnectedCallback() {
      this.stickyRO?.disconnect();
    }

    selected() {
      return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value ?? null);
    }

    onOptionChange(changedIndex) {
      const sel = this.selected();
      let variant = this.variants.find((v) => v.options.every((o, i) => o === sel[i]));

      if (!variant) {
        // combination doesn't exist: keep the value just picked, match the rest as closely as possible
        let best = null;
        let bestScore = -1;
        this.variants.forEach((v) => {
          if (v.options[changedIndex] !== sel[changedIndex]) return;
          const score = v.options.filter((o, i) => o === sel[i]).length * 2 + (v.available ? 1 : 0);
          if (score > bestScore) {
            best = v;
            bestScore = score;
          }
        });
        variant = best;
        if (variant) this.checkOptions(variant.options);
      }
      this.setVariant(variant);
    }

    checkOptions(options) {
      this.fieldsets.forEach((fs, i) => {
        fs.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = input.value === options[i];
        });
      });
    }

    setVariant(variant) {
      const sel = this.selected();
      this.fieldsets.forEach((fs, i) => {
        const label = fs.querySelector('[data-cx-selected]');
        if (label) label.textContent = sel[i] ?? '';
      });
      this.querySelector('.product-form__error-message-wrapper')?.setAttribute('hidden', '');

      if (!variant) {
        this.setButton(false, this.strings.unavailable);
        if (this.idInput) this.idInput.disabled = true;
        if (this.stockEl) this.stockEl.hidden = true;
        this.updateAvailability();
        return;
      }

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.disabled = !variant.available;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.setButton(variant.available, variant.available ? this.submit?.dataset.label : this.strings.soldOut);

      if (this.priceEl) {
        let html = `<span class="cx-price__now">${variant.price}</span>`;
        if (variant.compare) {
          html += `<s class="cx-price__was">${variant.compare}</s>`;
          if (this.priceEl.dataset.showSave === 'true' && variant.save > 0) html += `<span class="cx-price__save">-${variant.save}%</span>`;
        }
        this.priceEl.innerHTML = html;
      }

      if (this.skuEl) {
        this.skuEl.hidden = !variant.sku;
        this.skuEl.querySelector('span').textContent = variant.sku || '';
      }

      if (this.stockEl) {
        const max = Number(this.stockEl.dataset.threshold) || 10;
        const low = variant.available && variant.qty !== null && variant.qty > 0 && variant.qty <= max;
        this.stockEl.hidden = !low;
        if (low) this.stockEl.querySelector('[data-cx-stock-text]').textContent = (this.stockEl.dataset.text || '').replace('[count]', variant.qty);
      }

      if (variant.media) this.showMedia(variant.media);
      if (this.dataset.updateUrl === 'true') window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      this.updateAvailability();
    }

    setButton(enabled, text) {
      if (!this.submit) return;
      this.submit.disabled = !enabled;
      this.submit.removeAttribute('aria-disabled');
      const span = this.submit.querySelector('span');
      if (span) {
        span.classList.remove('hidden');
        span.textContent = text || '';
      }
    }

    updateAvailability() {
      const sel = this.selected();
      this.fieldsets.forEach((fs, i) => {
        fs.querySelectorAll('input[type="radio"]').forEach((input) => {
          const available = this.variants.some(
            (v) => v.available && v.options.every((o, k) => (k === i ? o === input.value : o === sel[k]))
          );
          fs.querySelector(`label[for="${input.id}"]`)?.classList.toggle('is-unavailable', !available);
        });
      });
    }

    showMedia(mediaId) {
      if (!this.gallery) return;
      const item = this.gallery.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      if (this.thumbs) {
        this.goTo(this.items.indexOf(item));
        return;
      }
      this.gallery.querySelectorAll('.cx-gal__item').forEach((el) => {
        el.classList.remove('is-lead');
        el.style.order = '';
      });
      item.classList.add('is-lead');
      item.style.order = '-1';
      this.gallery.scrollTo({ left: 0, behavior: 'smooth' });
      if (this.countEl) this.countEl.textContent = '1';
      this.setBar(0);
    }
  }

  customElements.define('cx-product', CxProduct);
}